import {
  DropdownMenu,
  DropdownMenuItem,
} from '@/components/Elements/DropdownMenu';
import { ChevronDownIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { getCurrentLanguage } from '@/lib/i18n/getCurrentLanguage';
import { type SupportedLanguage } from '@/lib/i18n/config';
import {
  getLanguageI18nKey,
  getLanguageOptions,
} from '@/lib/i18n/languageLabels';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';

type Props = {
  theme?: 'black' | 'white';
  variant?: 'header' | 'settings';
};

export const LanguageSwitcher = ({
  theme = 'black',
  variant = 'header',
}: Props) => {
  const { t } = useTranslation();
  const currentLanguage = t(getLanguageI18nKey(getCurrentLanguage()));
  const languageOptions = getLanguageOptions(t);

  const handleLanguageChange = (languageKey: SupportedLanguage) => {
    i18n.changeLanguage(languageKey);
  };

  // 設定画面用のシンプルなプルダウン
  if (variant === 'settings') {
    return (
      <DropdownMenu
        trigger={
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2"
          >
            <span>{currentLanguage}</span>
            <ChevronDownIcon className="size-4 text-gray-500" />
          </button>
        }
        className="z-50"
      >
        {languageOptions.map((option) => (
          <DropdownMenuItem
            key={option.value}
            onClick={() => handleLanguageChange(option.value)}
          >
            {option.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenu>
    );
  }

  // ヘッダー用の元のデザイン
  return (
    <DropdownMenu
      trigger={
        <div
          className={clsx(
            'relative flex cursor-default items-center gap-x-1.5 rounded-2xl px-3 py-1.5 ring-1 ring-inset',
            theme === 'black' ? 'ring-black' : 'text-white ring-white'
          )}
        >
          <GlobeAltIcon className="hidden size-6 sm:block" />
          {currentLanguage}
          <ChevronDownIcon className="size-3" />
        </div>
      }
      className="z-50"
      matchTriggerWidth
    >
      {languageOptions.map((option) => (
        <DropdownMenuItem
          key={option.value}
          onClick={() => handleLanguageChange(option.value)}
        >
          {option.label}
        </DropdownMenuItem>
      ))}
    </DropdownMenu>
  );
};
